import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dataManagementApi } from '../services/api';
import { recalculateAllRiskScores, type RecalculateAllResult } from '../utils/riskScoring';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';

export const SettingsPage: React.FC = () => {
  const tenantId = localStorage.getItem('tenantId') || '';
  const userRole = localStorage.getItem('userRole') || '';
  const isAdmin = userRole === 'admin';

  const toast = useToast();
  const swal = useSwal();
  const queryClient = useQueryClient();

  // Deletion confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');

  const exportMutation = useMutation({
    mutationFn: () => dataManagementApi.exportData(),
    onSuccess: (res) => {
      if (res?.downloadUrl) {
        window.open(res.downloadUrl, '_blank');
        toast.success('Your data export is ready. The download link will expire in 1 hour.');
      }
    },
    onError: (e: Error) => toast.error(`Export failed: ${e.message}`),
  });

  const recalculateMutation = useMutation({
    mutationFn: (): Promise<RecalculateAllResult> => recalculateAllRiskScores(),
    onSuccess: (result) => {
      // The app's default staleTime is 5 minutes (App.tsx) - without this, a Risk register list
      // or Risk Reports tab already cached from before this ran would keep showing pre-recalc
      // numbers for up to 5 minutes even though the backend is now correct, which is exactly
      // what reads as "still not fixed" if checked right after clicking this. Broad prefix match
      // ('list' matches every ['list', listPath, registerId] entry, not just risks) since Risk
      // isn't the only register type this could ever matter for.
      queryClient.invalidateQueries({ queryKey: ['list'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success(`Checked ${result.total} risks — updated ${result.updated}${result.failed ? `, ${result.failed} failed (see console)` : ''}.`);
    },
    onError: (e: Error) => toast.error(`Recalculation failed: ${e.message}`),
  });

  const deleteAccountMutation = useMutation({
    mutationFn: () => dataManagementApi.deleteAccount('permanently delete'),
    onSuccess: (res) => {
      const date = res?.scheduledDeletionDate
        ? new Date(res.scheduledDeletionDate).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })
        : '30 days from now';
      setShowDeleteConfirm(false);
      setDeleteInput('');
      swal.confirm(
        'Account deletion scheduled',
        `Your account and all data will be permanently deleted on ${date}. You will receive a confirmation email. You may still log in until then.`
      );
    },
    onError: (e: Error) => toast.error(`Deletion request failed: ${e.message}`),
  });

  const handleDeleteAccount = () => {
    if (deleteInput !== 'permanently delete') {
      toast.error('Please type "permanently delete" exactly to confirm.');
      return;
    }
    deleteAccountMutation.mutate();
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader icon="ri-settings-3-line" title="Settings" />

      {/* ── Maintenance — admin only ─────────────────────────────────── */}
      {isAdmin && (
        <div className="np-card">
          <div className="np-card-head">
            <h2>Maintenance</h2>
          </div>
          <div className="np-card-body">
            <div className="border rounded p-3">
              <h6 className="mb-1">Recalculate Risk Scores</h6>
              <p className="np-hint mb-3">
                A Risk's Inherent/Residual score normally updates on its own — whenever you open that
                Risk, or save a Control linked to it. Run this once if you need every existing Risk
                brought up to date immediately instead of waiting for either of those (for example,
                right after a scoring change ships). Only Risks whose score has actually changed get
                written.
              </p>
              <button
                className="np-btn np-btn-ghost"
                onClick={() => recalculateMutation.mutate()}
                disabled={recalculateMutation.isPending}
              >
                {recalculateMutation.isPending ? (
                  <><span className="spinner-border spinner-border-sm" role="status" />Recalculating…</>
                ) : (
                  <><i className="ri-refresh-line" />Recalculate All Risk Scores</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Data & Privacy ────────────────────────────────────────────── */}
      <div className="np-card">
        <div className="np-card-head">
          <h2>Data &amp; Privacy</h2>
        </div>
        <div className="np-card-body">
          <p className="np-sub mb-4">
            In line with GDPR, you can download a copy of all data held in your account, or request permanent deletion.
          </p>

          {/* Export */}
          <div className="border rounded p-3 mb-3">
            <h6 className="mb-1">Download Your Data</h6>
            <p className="np-hint mb-3">
              Export all your organisation's data (risks, controls, evidence, incidents, users, etc.) as a ZIP of CSV files.
              The download link is valid for 1 hour.
            </p>
            <button
              className="np-btn np-btn-ghost"
              onClick={() => exportMutation.mutate()}
              disabled={exportMutation.isPending || !tenantId}
            >
              {exportMutation.isPending ? (
                <><span className="spinner-border spinner-border-sm" role="status" />Preparing export…</>
              ) : (
                <><i className="ri-download-2-line" />Download My Data (CSV)</>
              )}
            </button>
            {exportMutation.error && (
              <div className="np-banner np-tone-danger mt-2 mb-0">
                <i className="ri-error-warning-line"></i>Export failed. Please try again.
              </div>
            )}
          </div>

          {/* Delete Account — admin only */}
          {isAdmin && (
            <div className="border border-danger rounded p-3">
              <h6 className="text-danger mb-1">Delete Account</h6>
              <p className="np-hint mb-3">
                Permanently delete your organisation, all users, and all associated data.
                This action is irreversible. Key things to know:
              </p>
              <ul className="np-hint mb-3">
                <li>Deletion is <strong>scheduled 30 days from now</strong> — you and all users can continue using the account until then.</li>
                <li>All accounts will be <strong>permanently removed on the deletion date</strong>.</li>
                <li>Download a copy of your data below before the deletion date if you need it.</li>
              </ul>

              {!showDeleteConfirm ? (
                <button
                  className="np-btn np-btn-ghost-danger"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <i className="ri-delete-bin-line" />Request Account Deletion
                </button>
              ) : (
                <div className="border border-danger rounded p-3 np-tone-danger">
                  <p className="fw-semibold mb-2">
                    Are you sure? Type <code>permanently delete</code> below to confirm.
                  </p>
                  <input
                    type="text"
                    className="np-input mb-3"
                    placeholder='Type "permanently delete" to confirm'
                    value={deleteInput}
                    onChange={(e) => setDeleteInput(e.target.value)}
                    autoComplete="off"
                  />
                  <div className="d-flex gap-2">
                    <button
                      className="np-btn np-btn-danger"
                      onClick={handleDeleteAccount}
                      disabled={deleteAccountMutation.isPending || deleteInput !== 'permanently delete'}
                    >
                      {deleteAccountMutation.isPending ? (
                        <><span className="spinner-border spinner-border-sm" role="status" />Processing…</>
                      ) : 'Confirm — Delete My Account'}
                    </button>
                    <button
                      className="np-btn np-btn-ghost"
                      onClick={() => { setShowDeleteConfirm(false); setDeleteInput(''); }}
                      disabled={deleteAccountMutation.isPending}
                    >
                      Cancel
                    </button>
                  </div>
                  {deleteAccountMutation.error && (
                    <div className="np-banner np-tone-danger mt-3 mb-0">
                      <i className="ri-error-warning-line"></i>Request failed. Please try again or contact support.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
